import {
  EnumRichiestaDTOStato,
  EnumTipoOnereDTOTipoVincolo,
  ErrorType,
  RichiestaDTO,
  useGetFrontRichiesteIdOneri,
  useGetFrontRichiesteIdPagamenti,
  useGetFrontRichiesteIdPreventivi,
} from '@framework/api-client';
import { usePrevious } from '@framework/form';
import {
  CompilationContainer,
  ISubmissionNavigator,
  SUBMISSION_TITLE_FOCUS_ID,
  SubmissionBurdenCardFooter,
} from '@framework/frontoffice-submission';
import { useTranslation } from '@framework/i18n';
import { LoaderSpin, ResponsiveCard } from '@framework/ui';
import { Alert, Divider, message } from 'antd';
import Paragraph from 'antd/es/typography/Paragraph';
import Title from 'antd/es/typography/Title';
import qs from 'qs';
import React, { useEffect, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import { invalidateSubmissionQueries } from '../submission/invalidateSubmissionQueries';
import { BurdenSelectionTable } from './BurdenSelectionTable';
import { PaymentsTable } from './PaymentsTable';
import { ISubmissionQuote } from './quote/ISubmissionQuote';
import { QuoteTable } from './quote/QuoteTable';
import { SubmissionPaymentsLogic } from './SubmissionPaymentsLogic';

const StyledResponsiveCard = styled(ResponsiveCard)`
  border: 1px solid ${props => props.theme.colorSplit};
  font-size: 18px;
`;

/**
 * Wrapper di utility per permettere di nascondere la card in base a una condizione
 */
function StyledResponsiveCardWrapper(props: {
  children: React.ReactNode;
  hideCard: boolean;
}) {
  if (props.hideCard) return <>{props.children}</>;
  return <StyledResponsiveCard>{props.children}</StyledResponsiveCard>;
}

interface SubmissionPaymentsCardProps {
  submissionId: string | null | undefined;
  handleNavigation: ISubmissionNavigator | null | undefined;
  renderError?: (error: ErrorType<unknown>) => React.ReactNode;
  readonly?: boolean;
  submission: RichiestaDTO | null | undefined;
  hideCard?: boolean;
}

/**
 * gestisce la risposta del pagamento
 */
function parsePaymentResponse(location: string) {
  const operationResponse = qs.parse(location, {
    ignoreQueryPrefix: true,
  });

  // gestione caso particolare: esito bollo è impostato a "successo" ma esito è impostato a "ERROR"
  // "successo" è il valore che impostiamo di default in `urlRitorno`
  if (
    String(operationResponse.esitoBollo).toLowerCase() == 'error' ||
    String(operationResponse.esito).toLowerCase() == 'error'
  ) {
    return 'error';
  }

  const paymentResponseStatus =
    operationResponse.esitoBollo ?? operationResponse.esito;

  if (Array.isArray(paymentResponseStatus)) {
    return null;
  }

  const paymentResponseStatusLower = String(
    paymentResponseStatus,
  ).toLowerCase();

  if (
    paymentResponseStatusLower === 'successo' ||
    paymentResponseStatusLower === 'success' ||
    paymentResponseStatusLower === 'OK'
  ) {
    return 'success';
  }

  if (paymentResponseStatusLower === 'indietro') {
    return 'back';
  }

  return null;
}

export function SubmissionPaymentsCard(props: SubmissionPaymentsCardProps) {
  const {
    submissionId,
    handleNavigation,
    renderError,
    readonly = false,
    submission,
    hideCard = false,
  } = props;

  const location = useLocation();
  const { t } = useTranslation();

  const paymentResponseStatus = useMemo(
    () => parsePaymentResponse(location.search),
    [location.search],
  );

  useEffect(() => {
    if (paymentResponseStatus === 'success') {
      message.success(
        t(
          'submission-payment-form.pagamento-completato-con-successo',
          'Pagamento completato con successo',
        ),
      );
    }
    if (paymentResponseStatus === 'back') {
      message.info(
        t('submission-payment-form.pagamento-annullato', 'Pagamento annullato'),
      );
    }
    if (paymentResponseStatus === 'error') {
      message.error(
        t(
          'submission-payment-form.pagamento-errore',
          'Si è verificato un errore durante il pagamento',
        ),
      );
    }
  }, [paymentResponseStatus, t]);

  const burdensQuery = useGetFrontRichiesteIdOneri(
    submissionId ?? '<unknown>',
    { query: { keepPreviousData: true, enabled: Boolean(submissionId) } },
  );

  const paymentsQuery = useGetFrontRichiesteIdPagamenti(
    submissionId ?? '<unknown>',
    {
      query: {
        enabled: Boolean(submissionId),
        keepPreviousData: true,
        refetchInterval: fetchedPayments =>
          SubmissionPaymentsLogic.hasPaymentsAwaitingOutcome(fetchedPayments)
            ? 5000
            : false,
      },
    },
  );

  const queryClient = useQueryClient();
  const hasPaymentsAwaitingOutcome =
    SubmissionPaymentsLogic.hasPaymentsAwaitingOutcome(paymentsQuery.data);
  const paymentsOutcomeJustArrived =
    usePrevious(hasPaymentsAwaitingOutcome) && !hasPaymentsAwaitingOutcome;

  useEffect(() => {
    // Solo la query dei pagamenti fa polling: quando i pagamenti pendenti
    // ricevono l'esito, richiesta, oneri e preventivi mostrati in pagina sono
    // ormai stantii; invalidiamo le query della richiesta per allineare
    // banner, stati e menu senza un refresh manuale della pagina
    if (paymentsOutcomeJustArrived && submissionId) {
      invalidateSubmissionQueries(queryClient, submissionId);
    }
  }, [paymentsOutcomeJustArrived, submissionId, queryClient]);

  const burdens = useMemo(
    () => burdensQuery.data?.filter(b => !b.tipoOnere?.flPagamentoBollo) ?? [],
    [burdensQuery.data],
  );
  const stampDutyBurdens = useMemo(
    () => burdensQuery.data?.filter(b => b.tipoOnere?.flPagamentoBollo) ?? [],
    [burdensQuery.data],
  );
  const payments = useMemo(
    () => paymentsQuery.data?.filter(p => p.flValido) ?? [],
    [paymentsQuery.data],
  );

  // const quotes = fixuturesPreventivi;
  const quotesResult = useGetFrontRichiesteIdPreventivi(
    submissionId ?? '<unknown>',
    { query: { keepPreviousData: true, enabled: Boolean(submissionId) } },
  );

  const quotes = useMemo(
    () => quotesResult.data ?? [],
    [quotesResult.data],
  ) as ISubmissionQuote[];

  const isAllAfterSendBurdens =
    burdens.length &&
    burdens.every(
      b =>
        b.tipoOnere?.tipoVincolo ===
        EnumTipoOnereDTOTipoVincolo.DA_PAGARE_DOPO_INVIO,
    );
  const isAllAfterSendStampDutyBurdens =
    burdens.length &&
    stampDutyBurdens.every(
      b =>
        b.tipoOnere?.tipoVincolo ===
        EnumTipoOnereDTOTipoVincolo.DA_PAGARE_DOPO_INVIO,
    );
  const hidePayments =
    isAllAfterSendBurdens &&
    isAllAfterSendStampDutyBurdens &&
    submission?.stato !== EnumRichiestaDTOStato.INVIATA &&
    submission?.stato !== EnumRichiestaDTOStato.RICEVUTA;

  const burdensHelpText = isAllAfterSendBurdens
    ? t(
        'submission-payment-form.oneri-bolli-da-pagare-dopo-invio',
        'Di seguito trovi i pagamenti da effettuare a seguito dell’invio',
      )
    : t(
        'submission-payment-form.seleziona-oneri',
        'Seleziona gli oneri per procedere al pagamento',
      );

  const stampDutyHelpText = isAllAfterSendStampDutyBurdens
    ? t(
        'submission-payment-form.oneri-bolli-da-pagare-dopo-invio',
        'Di seguito trovi i pagamenti da effettuare a seguito dell’invio',
      )
    : t(
        'submission-payment-form.seleziona-bolli',
        'Seleziona i bolli per procedere al pagamento',
      );

  if (
    burdensQuery.isLoading ||
    paymentsQuery.isLoading ||
    quotesResult.isLoading
  ) {
    return <LoaderSpin transparent full />;
  }

  if (burdensQuery.isError || paymentsQuery.isError || quotesResult.isError) {
    return (
      renderError?.(
        (burdensQuery.error ?? paymentsQuery.error ?? quotesResult.error)!,
      ) ?? null
    );
  }

  if (!submission) {
    return (
      <CompilationContainer>
        <Alert
          type="error"
          showIcon
          message={t(
            'submission-payment-form.errore-richiesta-titolo',
            'Richiesta non definita',
          )}
          description={t(
            'submission-payment-form.errore-richiesta',
            'Si è verificato un errore durante il caricamento della richiesta. La richiesta non è definita.',
          )}
          style={{ marginBottom: 24 }}
        />
      </CompilationContainer>
    );
  }

  return (
    <>
      <h1 className="sr-only" id={SUBMISSION_TITLE_FOCUS_ID} tabIndex={-1}>
        {t('menu-items.pagamenti', 'Pagamenti')}
      </h1>
      <CompilationContainer>
        {paymentResponseStatus === 'success' && (
          <Alert
            type="success"
            showIcon
            message={t(
              'submission-payment-form.pagamento-completato',
              'Il pagamento è stato completato con successo',
            )}
            style={{ marginBottom: 24 }}
          />
        )}

        {paymentResponseStatus === 'error' && (
          <Alert
            type="error"
            showIcon
            message={t(
              'submission-payment-form.pagamento-errore',
              'Si è verificato un errore durante il pagamento',
            )}
            style={{ marginBottom: 24 }}
          />
        )}

        <StyledResponsiveCardWrapper hideCard={hideCard}>
          {quotes.length > 0 && (
            <div>
              <Title level={3}>
                {t('submission-menu-item.preventivi', 'Preventivi')}
              </Title>
              <QuoteTable
                readonly={readonly}
                quotes={quotes}
                submissionId={submissionId}
              />
              <Divider />
            </div>
          )}
          {burdens.length > 0 && (
            <>
              <Title level={3}>
                {t('submission-menu-item.oneri', 'Oneri')}
              </Title>
              {!readonly && <Paragraph>{burdensHelpText}</Paragraph>}
              <BurdenSelectionTable burdens={burdens} readonly={readonly} />
            </>
          )}

          {stampDutyBurdens.length > 0 && (
            <>
              {burdens.length > 0 && <Divider />}
              <Title level={3}>
                {t('submission-menu-item.bolli', 'Bolli')}
              </Title>
              {!readonly && <Paragraph>{stampDutyHelpText}</Paragraph>}
              <BurdenSelectionTable
                burdens={stampDutyBurdens}
                readonly={readonly}
              />
            </>
          )}

          {/* {isAllAfterSendBurdens && isAllAfterSendStampDutyBurdens ? (
            <div>
              <Divider />
              <Title level={3}>
                {t('frontoffice-submission:submission-menu-item.pagamenti', 'Pagamenti')}
              </Title>
              <Paragraph>
                {t(
                  'front:submission-form.inviare-per-procedere-al-pagamento',
                  "Procedere all'invio della Richiesta per effettuare il pagamento degli oneri.",
                )}
              </Paragraph>
            </div>
          ) : ( */}
          {!hidePayments && (
            <div>
              <Divider />

              <Title level={3}>
                {t('submission-menu-item.pagamenti', 'Pagamenti')}
              </Title>
              <PaymentsTable
                payments={payments}
                readonly={readonly}
                submission={submission}
              />
            </div>
          )}
        </StyledResponsiveCardWrapper>
      </CompilationContainer>

      {handleNavigation && (
        <SubmissionBurdenCardFooter
          handleNavigation={handleNavigation}
          readonly={readonly}
        />
      )}
    </>
  );
}
